import {
  within,
  canAccess,
  activeRoute,
  SALE_EXPENSES_DISABLED_REASON,
} from '@evogard/domain';
import type { IconName } from '@evogard/ui';

/**
 * A navegação da interna, escrita à mão.
 *
 * São 7 módulos, não 70: uma linha aqui é mais barata e muito mais fácil de
 * auditar do que um catálogo que se monta sozinho. E, como o catálogo de
 * permissões e o registro de filas, é conferido por teste — o
 * `registry.test.ts` reprova recurso que não existe, ação que o catálogo não
 * declara e item apontando para rota que ninguém registrou.
 *
 * ## Por que virou três níveis
 *
 * Era uma lista plana de seis itens, e a lista plana estava certa para seis. O
 * plano tem ~32 telas internas em oito áreas — comercial 6, cobrança 5,
 * plataforma 5, gestão 4, demandas 3, erp 3, retenção 2, portal 1. Uma coluna
 * com 32 links é uma coluna que se rola para achar o que se usa todo dia.
 *
 * Então: **módulo → seção → item**. O módulo é o que aparece no rail (a coluna
 * estreita), a seção é o rótulo dentro da barra, e o item é o link. O mesmo
 * array responde quatro perguntas — o que desenhar no rail, o que desenhar na
 * barra, qual módulo está aberto e para onde o clique no rail leva.
 *
 * ## As três regras deste arquivo
 *
 * 1. **`prefixos` é separado de `inicio`.** "Quais rotas pertencem a este
 *    módulo" e "para onde o rail me leva" são perguntas diferentes: demandas
 *    tem três telas e um destino. Amarrar as duas faz o rail apagar quando a
 *    pessoa está numa tela de detalhe.
 * 2. **Módulo não existe para servir um item só.** Se uma área tem um link,
 *    ela é seção dentro de outro módulo — é por isso que "Associados do portal"
 *    mora em Plataforma e não tem slot próprio no rail. Um ícone no rail é um
 *    lugar que a pessoa precisa aprender; ele tem que pagar por si.
 * 3. **Item sem `recurso` aparece para todo mundo que está logado.** Com
 *    `recurso`, some para quem não pode — e some em cascata: seção sem item
 *    não desenha rótulo, módulo sem seção não aparece no rail.
 * 4. **Ícone é do MÓDULO, e só dele.** O item teve um até 16/09, e a referência
 *    da lateral nova não desenha ícone em subitem: ele leva um ponto, cheio na
 *    tela aberta e vazado no resto, dentro do bloco recuado. O campo saiu junto
 *    com a última tela que o lia — campo que nenhuma tela desenha é campo que
 *    envelhece sem ninguém notar.
 */

export interface MenuItem {
  label: string;
  to: string;
  resource?: string;
  action?: string;
  /**
   * A `entity` de `app.notifications` cujos avisos não lidos acendem o ponto
   * deste item, em laranja.
   *
   * ## O mapeamento é PARCIAL, e de propósito
   *
   * `entity` é `text` livre na tabela — hoje há `ticket`, `retention_request`,
   * `billing_boleto`, `user`, `portal_invite` e `portal_password` gravados por
   * ali. Aviso cuja entidade não casa com item nenhum **não acende nada, em
   * silêncio**.
   *
   * Isso é inofensivo hoje porque `ticket` é a única entidade que chama
   * `recordPanel` — o canal `painel`, que é o que o sino mostra. Deixa de ser
   * no dia em que cobrança ou retenção passarem a gravar por lá: o aviso
   * apareceria no sino e o rail continuaria apagado, e a pessoa aprenderia que o
   * ponto não é confiável.
   *
   * Quem acrescentar o próximo `recordPanel` acende o ponto declarando a
   * entidade aqui. Este docblock existe para que essa linha seja encontrada
   * antes, e não depois.
   */
  notificationEntity?: string;
  /**
   * Só para quem tem vínculo de consultor na base atual (`context.consultant`).
   *
   * Vale POR CIMA da permissão, e não no lugar dela: `:ver_meus` costuma vir
   * do grupo, e o grupo junta consultor e quem não é. Sem o vínculo a tela não
   * tem nada da pessoa para mostrar — o item seria uma porta para um quadro
   * vazio. Esconder continua sendo cortesia: quem protege é a API, que
   * responde pelo `ctx.user.id`.
   */
  consultantOnly?: boolean;
  /**
   * O inverso de `consultantOnly`: some para quem tem vínculo de consultor na
   * base atual. Existe para o Início da plataforma (28/10/2026, pedido do
   * dono) — o consultor começa em Comercial › Início, e a raiz o manda para lá —
   * e para a relação de Intervalos, que o consultor não tira.
   */
  hiddenForConsultant?: boolean;
  /**
   * Um segundo par que também mostra o item — para a tela que tem dois
   * públicos com permissões diferentes. A configuração do intervalo é de quem
   * edita a base (`breaks.settings:editar`) E do líder de departamento
   * (`breaks.records:liderar`), que lá vê só a equipe. Quem decide o que cada
   * um vê dentro da tela é a API; aqui é só o caminho até ela.
   *
   * Aceita uma lista desde 26/10/2026: as Despesas do sinistro são um item só,
   * e cada departamento supervisiona pelo próprio recurso — quem supervisiona
   * qualquer um deles vê o item, e a tela mostra só os seus.
   */
  alternative?: MenuGrant | readonly MenuGrant[];
}

/** Um par recurso × ação que mostra o item. */
export interface MenuGrant {
  resource: string;
  action: string;
}

export interface MenuSection {
  /** Sem rótulo, os itens ficam soltos no topo — é o caso da primeira seção. */
  label?: string;
  items: MenuItem[];
  resource?: string;
  action?: string;
}

export interface MenuModule {
  id: string;
  label: string;
  icon: IconName;
  /** As rotas que pertencem a este módulo. Decide qual rail acende. */
  prefixes: string[];
  /** Para onde o clique no rail leva. */
  start: string;
  sections: MenuSection[];
  /** `rodape` empurra para o fim do rail. Configuração fica embaixo. */
  position?: 'principal' | 'rodape';
  resource?: string;
  action?: string;
}

export const MODULES: MenuModule[] = [
  {
    id: 'inicio',
    label: 'Início',
    icon: 'inicio',
    prefixes: ['/'],
    start: '/',
    sections: [{ items: [{ label: 'Início', to: '/', hiddenForConsultant: true }] }],
  },
  {
    id: 'demandas',
    label: 'Ticket',
    icon: 'demandas',
    prefixes: ['/tickets'],
    start: '/tickets',
    resource: 'tickets.tickets',
    action: 'ver',
    sections: [
      {
        items: [
          {
            label: 'Tickets',
            to: '/tickets',
            resource: 'tickets.tickets',
            action: 'ver',
            /* o mesmo valor que `TicketsProcessor` grava em `app.notifications` */
            notificationEntity: 'ticket',
          },
          {
            /*
             * Mesmo `tickets.tickets:ver` da mesa: o relatório conta o que ela
             * lista, sobre o mesmo recorte. Um recurso próprio faria a tela de
             * grupos oferecer uma separação que o sistema não faz.
             */
            label: 'Relatório',
            to: '/tickets/report',
            resource: 'tickets.tickets',
            action: 'ver',
          },
        ],
      },
      {
        label: 'Configuração',
        items: [
          {
            label: 'Categorias',
            to: '/tickets/categories',
            resource: 'tickets.categories',
            action: 'ver',
          },
        ],
      },
    ],
  },
  {
    id: 'erp',
    label: 'ERP',
    icon: 'erp',
    prefixes: ['/erp'],
    start: '/erp/saude',
    /*
     * Dois itens, e por isso é módulo do rail e não seção dentro de outro.
     *
     * A regra 2 deste arquivo diz que um ícone no rail tem que pagar por si:
     * área com um link é seção dentro de outro módulo. Aqui são duas telas de
     * assuntos diferentes — o estado da esteira, que se olha todo dia, e a
     * classificação de situações, que se ajusta de vez em quando —, e as duas
     * crescem com o módulo (reconciliação e escritas em verificação vêm depois).
     *
     * O recurso é `erp.mirror` nos dois níveis: um typo aqui faria o módulo
     * sumir do rail para todo mundo sem erro em lugar nenhum, porque
     * `canAccess` nega por omissão — corretamente. `registry.test.ts` compara
     * este arquivo com o catálogo da API justamente por isso.
     *
     * `erp.sync` não aparece: ela não tem tela própria, é o botão
     * "atualizar agora" dentro da saúde. Ação declarada sem lugar no menu é
     * diferente de ação sem rota — esta tem rota, e quem a concede vê o efeito
     * no botão.
     */
    resource: 'erp.mirror',
    action: 'ver',
    sections: [
      {
        items: [
          {
            label: 'Saúde da integração',
            to: '/erp/saude',
            resource: 'erp.mirror',
            action: 'ver',
          },
        ],
      },
      /*
       * O cadastro, com recurso PRÓPRIO — `erp.catalog`, e não `erp.mirror`.
       *
       * Quem enxerga o estado das esteiras não recebe junto o cadastro de 52 mil
       * pessoas com nome, telefone e endereço: são concessões de tamanhos
       * diferentes, e a seção inteira some para quem não tem a segunda.
       *
       * O módulo continua aparecendo no rail por `erp.mirror` — quem só tem
       * `erp.catalog` NÃO chega aqui, e isso é um buraco declarado: o `resource`
       * do módulo é um só. Resolver pede que o menu aceite "qualquer um destes",
       * que é mudança em `menu.ts` e não neste bloco.
       */
      {
        label: 'Cadastro',
        items: [
          {
            label: 'Associados',
            to: '/erp/associados',
            resource: 'erp.catalog',
            action: 'ver',
          },
          {
            label: 'Voluntários',
            to: '/erp/voluntarios',
            resource: 'erp.catalog',
            action: 'ver',
          },
          {
            label: 'Veículos',
            to: '/erp/veiculos',
            resource: 'erp.catalog',
            action: 'ver',
          },
          {
            label: 'Boletos',
            to: '/erp/boletos',
            resource: 'erp.catalog',
            action: 'ver',
          },
          {
            label: 'Produtos',
            to: '/erp/produtos',
            resource: 'erp.catalog',
            action: 'ver',
          },
          {
            label: 'Planos',
            to: '/erp/planos',
            resource: 'erp.catalog',
            action: 'ver',
          },
        ],
      },
      /*
       * O histórico do SGA (14/10/2026) — o mesmo recurso do cadastro: são
       * as mesmas pessoas e os mesmos boletos, vistos pelo que aconteceu com
       * eles.
       */
      {
        label: 'Histórico',
        items: [
          {
            label: 'Atendimentos',
            to: '/erp/atendimentos',
            resource: 'erp.catalog',
            action: 'ver',
          },
          {
            label: 'Alterações',
            to: '/erp/alteracoes',
            resource: 'erp.catalog',
            action: 'ver',
          },
        ],
      },
      {
        label: 'Configuração',
        items: [
          {
            label: 'Classificação de situações',
            to: '/erp/classificacoes',
            resource: 'erp.mirror',
            action: 'ver',
          },
          /* a lista do SGA que define os boletos — vocabulário, como a classificação acima */
          {
            label: 'Tipos de boleto',
            to: '/erp/tipos-boleto',
            resource: 'erp.catalog',
            action: 'ver',
          },
        ],
      },
    ],
  },
  {
    id: 'retencao',
    label: 'Kanban Cancelamento',
    icon: 'retencao',
    prefixes: ['/retention'],
    start: '/retention/board',
    /*
     * O Kanban Cancelamento (30/10/2026), o porte do esteira-cancelamento. As
     * seções ficam sob ações diferentes do mesmo recurso — o quadro é de quem
     * vê, a escala e as etiquetas de quem configura, o relatório de quem
     * exporta —, e a cascata esconde o que a pessoa não pode.
     */
    sections: [
      {
        items: [
          {
            label: 'Quadro',
            to: '/retention/board',
            resource: 'retention.requests',
            action: 'ver',
          },
          {
            label: 'Relatório',
            to: '/retention/report',
            resource: 'retention.requests',
            action: 'exportar',
          },
        ],
      },
      {
        label: 'Configuração',
        items: [
          {
            label: 'Escala e rodízio',
            to: '/retention/operators',
            resource: 'retention.requests',
            action: 'configurar',
          },
          {
            label: 'Etiquetas',
            to: '/retention/labels',
            resource: 'retention.requests',
            action: 'configurar',
          },
        ],
      },
    ],
  },
  {
    id: 'cobranca',
    label: 'Cobrança',
    icon: 'cobranca',
    prefixes: ['/billing'],
    start: '/billing/desk',
    /*
     * Três telas, e por isso o módulo paga pelo próprio slot no rail — a regra 2
     * deste arquivo. E são três assuntos, não três recortes do mesmo: a mesa é o
     * trabalho de todo dia, a esteira é o que a praça dispara sozinha, e a régua
     * é a configuração que decide o que a esteira faz.
     *
     * **Sem chave de permissão no módulo, e a ausência é a decisão** — a mesma
     * de retenção, pelo mesmo motivo e com uma consequência a mais aqui. As três
     * telas ficam sob recursos DIFERENTES, que o catálogo separou pelo tamanho
     * da consequência: operar a mesa não é parar a cobrança da praça, e nenhuma
     * das duas é substituir a régua. Declarar a chave da mesa aqui em cima
     * apagaria o ícone inteiro do rail para quem tem só a esteira — a pessoa
     * teria a permissão, a rota responderia, e não haveria caminho até ela.
     * Nenhum erro em lugar nenhum, que é o defeito que este arquivo persegue.
     *
     * A cascata já faz o certo sem a linha: cada item declara o que exige, seção
     * sem item não desenha rótulo e módulo sem seção não aparece no rail. E
     * `visibleModules` conserta o destino do clique — quem só tem a régua cai
     * nela, não num 403 na mesa.
     *
     * (O texto acima evita de propósito escrever as chaves em forma de código:
     * `registry.test.ts` lê este arquivo como TEXTO, e um exemplo dentro de
     * comentário entraria na lista que ele confere contra o catálogo.)
     */
    sections: [
      {
        items: [
          {
            label: 'Mesa',
            to: '/billing/desk',
            resource: 'billing.desk',
            action: 'ver',
          },
          /*
           * O aproveitamento da equipe (20/10/2026). Item próprio porque quem o
           * vê costuma ser a liderança, e a ação é outra.
           */
          {
            label: 'Aproveitamento',
            to: '/billing/desk/dashboard',
            resource: 'billing.desk',
            action: 'acompanhar',
          },
          /*
           * O log das movimentações dos cards, com as que vieram do Kanban
           * (20/10/2026). De quem acompanha, como o aproveitamento.
           */
          {
            label: 'Movimentações',
            to: '/billing/desk/moves',
            resource: 'billing.desk',
            action: 'acompanhar',
          },
          /*
           * A esteira é item próprio, e não uma aba dentro da mesa: ela responde
           * uma pergunta que a mesa não responde — o que a praça vai disparar
           * hoje, e o que NÃO vai e por quê. Quem confere isso costuma não ser
           * quem trata cartão, e a permissão separa os dois.
           */
          {
            label: 'Esteira',
            to: '/billing/pipeline',
            resource: 'billing.pipeline',
            action: 'ver',
          },
        ],
      },
      {
        /*
         * A premiação da cobrança (21/10/2026), que substitui a
         * `PremiacaoCobrancaRetencao`. Seção própria porque é outro assunto — o
         * dinheiro da equipe, e não o trabalho do card — e porque quem a vê é só a
         * liderança e a administração (decisão do dono).
         */
        label: 'Premiação',
        items: [
          {
            label: 'Painel e relatório',
            to: '/billing/awards',
            resource: 'billing.awards',
            action: 'ver',
          },
          {
            label: 'Relatório por competência',
            to: '/billing/awards/history',
            resource: 'billing.awards',
            action: 'ver',
          },
          {
            label: 'Monitoramento',
            to: '/billing/awards/links',
            resource: 'billing.awards',
            action: 'ver',
          },
          {
            label: 'Dados',
            to: '/billing/awards/data',
            resource: 'billing.awards',
            action: 'ver',
          },
          {
            label: 'Log',
            to: '/billing/awards/log',
            resource: 'billing.awards',
            action: 'ver',
          },
          {
            label: 'Configurar a premiação',
            to: '/billing/awards/settings',
            resource: 'billing.awards',
            action: 'ver',
          },
        ],
      },
      {
        /*
         * A régua é configuração, como "Fases e motivos" na retenção e
         * "Classificação de situações" no erp — não é um lugar onde se trabalha,
         * é onde se decide o que o trabalho automático faz. Pô-la na primeira
         * seção, ao lado da mesa, daria a ela o mesmo peso visual de uma tela
         * que se abre todo dia.
         *
         * E hoje ela é mais do que um ajuste: com `billing_tiers` vazia nas
         * duas praças, é por aqui que a cobrança automática de uma base é acesa
         * pela primeira vez.
         */
        label: 'Configuração',
        items: [
          {
            label: 'Régua de cobrança',
            to: '/billing/tiers',
            resource: 'billing.tiers',
            action: 'ver',
          },
          /*
           * A importação e a configuração da Mesa (19/10/2026). A importação é de
           * quem confere a prévia antes de pôr no quadro; a configuração diz quem
           * tem coluna e liga a conferência no ERP.
           */
          {
            label: 'Importar inadimplentes',
            to: '/billing/desk/import',
            resource: 'billing.desk',
            action: 'importar',
          },
          {
            label: 'Configurar a Mesa',
            to: '/billing/desk/settings',
            resource: 'billing.desk',
            action: 'configurar',
          },
        ],
      },
    ],
  },
  {
    id: 'comercial',
    label: 'Comercial',
    icon: 'comercial',
    /*
     * Os dois `/mine` estão aqui, e não só `/sales`, por causa do Financeiro:
     * ele declara `/sales/recurrence-payments` e `/sales/expenses`, e o rail
     * acende pelo prefixo MAIS ESPECÍFICO (`activeModule`). Sem estes dois, o
     * quadro do consultor acenderia o Financeiro — um módulo que ele nem vê.
     */
    prefixes: ['/sales', '/sales/recurrence-payments/mine', '/sales/expenses/mine'],
    start: '/sales/sales',
    /*
     * Sete telas, sete recursos DIFERENTES — e por isso **sem chave de
     * permissão no módulo**, como retenção e cobrança, mas aqui a ausência pesa
     * mais do que nas duas.
     *
     * O catálogo do comercial separou os sete pelo tamanho da consequência, não
     * por tela: registrar venda é trabalho de todo dia; ver a comissão apurada é
     * ver o dinheiro nominal de cada consultor da base; manter o cadastro é
     * decidir de quem a comissão é; e mexer na régua é decidir quanto cada placa
     * paga. Declarar aqui em cima a chave de qualquer uma delas apagaria o ícone
     * inteiro do rail para quem tem só outra — a pessoa teria a permissão, a
     * rota responderia, e não haveria caminho até ela. Nenhum erro em lugar
     * nenhum, que é o defeito que este arquivo persegue. Com sete recursos, a
     * chance de a chave escolhida ser a errada para quem está olhando é seis em
     * sete.
     *
     * A cascata já faz o certo sem a linha: cada item declara o que exige, seção
     * sem item não desenha rótulo e módulo sem seção não aparece no rail. E
     * `visibleModules` conserta o destino do clique — quem só tem o livro-caixa
     * cai nele, não num 403 nas vendas.
     *
     * (O texto acima evita de propósito escrever as chaves em forma de código:
     * `registry.test.ts` lê este arquivo como TEXTO, e um exemplo dentro de
     * comentário entraria na lista que ele confere contra o catálogo.)
     *
     * ## Por que três seções, e não uma lista de seis
     *
     * São três assuntos com plateias diferentes. "Trabalho" é o que a operação
     * comercial abre todo dia. "Remuneração" é o dinheiro de gente nomeada — e
     * separá-lo visualmente é a mesma cortesia que a permissão já faz por baixo.
     * "Configuração" é onde se decide o que o resto significa, como a régua na
     * cobrança e as fases na retenção.
     */
    sections: [
      {
        items: [
          {
            /*
             * O Início (28/10/2026): as premiações do consultor no mês, com a nota
             * fiscal de cada lote. Desde o mesmo dia ele é também o que o Painel
             * de Gestão era — o painel saiu do menu, e o endereço dele
             * redireciona para cá.
             */
            label: 'Início',
            to: '/sales/home',
            resource: 'sales.management',
            action: 'ver',
          },
          {
            /*
             * A Carteira, ao lado do painel e sob o MESMO recurso — de propósito.
             *
             * As duas telas leem a mesma carteira do mesmo consultor: o painel
             * responde "o que ela tem", e esta responde "quanto está em jogo
             * neste mês". Um recurso próprio faria a casa decidir duas vezes
             * quem vê a bonificação de quem, e a régua mais estreita venceria
             * sem ninguém perceber — que é o motivo pelo qual a visibilidade
             * por grupo da premiação saiu em 16/09.
             *
             * `ver` é o piso: quem tem só ele cai na própria carteira, e quem
             * tem `ver_qualquer` também. Desde 25/09/2026 quem tem
             * `ver_carteiras` cai na tabela de TODAS as carteiras, e abre a de
             * cada consultor por ela — o despacho mora em `WalletScreen`, pelo
             * mesmo motivo do Painel: um item só, com o nome da página.
             */
            label: 'BI',
            to: '/sales/metrics',
            resource: 'sales.management',
            action: 'ver',
          },
          {
            /*
             * A CALCULADORA (29/10/2026), que saiu das Métricas e ficou ao lado
             * delas, sob o MESMO recurso: a conta é do que a pessoa digita — só
             * as vendas e o churn nascem medidos da própria carteira —, e
             * quem planeja a recorrência é quem acompanha a própria carteira.
             */
            label: 'Calculadora',
            to: '/sales/calculator',
            resource: 'sales.management',
            action: 'ver',
          },
          {
            /*
             * O EXTRATO, vizinho da Carteira e recurso próprio.
             *
             * A Carteira responde "quanto entrou este mês"; este responde "de
             * onde", linha a linha. O recurso é `sales.statement` e não
             * `sales.management` porque conceder um não é conceder o outro: o
             * extrato desce ao nome do associado e à placa de cada entrada, e é
             * onde mora o ato de fechar a competência da praça.
             *
             * `ver` é o piso, e quem tem só ele cai no próprio extrato — quem
             * não é consultor nesta base vê a tela dizendo isso pelo nome, e
             * não uma lista vazia. Com `ver_qualquer` a barra ganha o seletor
             * de consultor. O ato de fechar saiu daqui em 25/09/2026 e foi para
             * o quadro de pagamentos — hoje Comercial › Pagamentos Recorrência.
             */
            label: 'Extrato',
            to: '/sales/statement',
            resource: 'sales.statement',
            action: 'ver',
          },
          {
            /*
             * A AGENDA de clientes, vizinha do Extrato e recurso próprio: ela
             * desce ao telefone de cada associado da carteira, e conceder o
             * painel de outro consultor não é conceder a lista de contatos dos
             * clientes dele. `ver` é o piso e abre a própria; com
             * `ver_qualquer` a tela abre na lista de agendas da base — o
             * despacho mora em `AgendaScreen`, como o do Painel de Gestão.
             */
            label: 'Carteira',
            to: '/sales/portfolio',
            resource: 'sales.agenda',
            action: 'ver',
          },
          {
            /*
             * O Relatório de Vendas (30/09/2026), logo ACIMA de "Vendas": a lista
             * responde "quais", e o relatório responde "quanto e de quem", sobre
             * o comercial inteiro da base — todo perfil de consultor. Recurso
             * próprio, sem leitura estreita: quem o recebe vê a praça.
             */
            label: 'Relatório de Vendas',
            to: '/sales/reports',
            resource: 'sales.reports',
            action: 'ver',
          },
          {
            label: 'Vendas',
            to: '/sales/sales',
            resource: 'sales.sales',
            action: 'ver',
          },
          {
            label: 'Metas',
            to: '/sales/targets',
            resource: 'sales.targets',
            action: 'ver',
          },
          /*
           * O ranking é item próprio e não uma aba das metas: ele é desempenho
           * de gente, nomeado e comparado em público, e a permissão que o abre é
           * outra. Hoje a tela responde que a casa não sabe ordenar — a P5 não
           * escolheu entre as duas contagens que rodam em produção —, e mesmo
           * assim o item fica: a ausência dita pelo nome dela é o que impede
           * alguém de reabrir o sistema antigo para "ver o ranking de verdade".
           */
          {
            label: 'Ranking',
            to: '/sales/ranking',
            resource: 'sales.ranking',
            action: 'ver',
          },
          /*
           * O painel do time vem LOGO DEPOIS do ranking, e os dois são itens
           * separados porque as permissões são duas.
           *
           * Até 18/09 o ranking trazia um segundo cartão somando por equipe.
           * Ele saiu com a ação que o mostrava, e a pergunta do líder — quem
           * produziu, o quê, e quais placas — virou esta tela. Um item só para
           * as duas faria o menu esconder o painel de quem tem só ele, ou
           * mostrar o ranking a quem não pode abri-lo: os dois recursos se
           * cruzam sem se conter.
           *
           * `ver` é o **piso das três leituras**: quem tem só ele vê os times
           * que lidera, `ver_meu_time` acrescenta a equipe em que a pessoa está,
           * e `ver_qualquer` dá a base inteira. O despacho não precisa de tela,
           * ao contrário do Painel de Gestão: aqui é a mesma página com uma
           * lista de opções maior.
           *
           * **É o piso que decide o item, e não ter time.** Quem tem `ver` e não
           * alcança equipe nenhuma vê o item e cai na tela que diz o que falta —
           * esconder o caminho faria a pessoa procurar a permissão que ela já
           * tem.
           */
          {
            label: 'Painel do time',
            to: '/sales/team',
            resource: 'sales.team_performance',
            action: 'ver',
          },
        ],
      },
      {
        label: 'Remuneração',
        items: [
          {
            label: 'Comissões',
            to: '/sales/commissions',
            resource: 'sales.commissions',
            action: 'ver',
          },
          /*
           * "Premiações" era um item aqui, logo abaixo de Comissões. Ele saiu
           * em 16/09/2026 com a tela: a premiação passou a pertencer a um
           * consultor, e cadastra-se dentro dele, em "Consultores e times ›
           * Editar". A permissão `sales.commissions:configurar_premiacao`
           * continua sendo a que a rota da API exige — quem não a tem vê a
           * seção em leitura.
           */
          /*
           * Os modelos de premiação (23/09/2026): a régua cadastrada uma vez e
           * vinculada aos consultores. A premiação continua morando na ficha de
           * cada um — vincular cria lá. Mesma ação que cadastra premiação.
           */
          {
            label: 'Modelos de premiação',
            to: '/sales/award-templates',
            resource: 'sales.commissions',
            action: 'configurar_premiacao',
          },
          {
            label: 'Livro-caixa',
            to: '/sales/finance',
            resource: 'sales.finance',
            action: 'ver',
          },
          /*
           * O quadro do consultor, só com os cartões dele (`:ver_meus`). O da
           * praça — "Pagamentos Recorrência" — mora no módulo Financeiro desde
           * 05/10/2026: lá trabalha quem confere e paga, e aqui fica o que é
           * do consultor. O ponto acende com os avisos do pagamento, que são
           * dele.
           */
          {
            label: 'Meus pagamentos',
            to: '/sales/recurrence-payments/mine',
            resource: 'management.payments',
            action: 'ver_meus',
            notificationEntity: 'management_payments',
            consultantOnly: true,
          },
          /*
           * A despesa lançada a partir de uma venda (02/10/2026), do lado do
           * consultor: as das vendas dele — o ponto acende com a devolução e o
           * pagamento. O quadro da praça ("Despesas de Venda") mora no
           * Financeiro. Fora do menu enquanto a despesa de venda estiver
           * desativada (`SALE_EXPENSES_DISABLED_REASON`, 23/10/2026).
           */
          ...(SALE_EXPENSES_DISABLED_REASON === null
            ? [
                {
                  label: 'Minhas despesas',
                  to: '/sales/expenses/mine',
                  resource: 'sales.expenses',
                  action: 'ver_minhas',
                  notificationEntity: 'sales_expenses',
                },
              ]
            : []),
        ],
      },
      {
        /*
         * Duas telas de configuração, e a ordem entre elas é a ordem em que a
         * casa precisa preenchê-las. Ficam aqui pelo mesmo motivo da régua da
         * cobrança: não é onde se trabalha, é onde se decide o que o trabalho
         * significa. A decisão do cadastro é **de quem** é a venda; a da régua é
         * **quanto** cada placa paga — e uma placa a mais no mês certo vale
         * R$ 250,00 na régua que o sistema antigo usa.
         *
         * A régua vem primeiro porque é ela que destrava o módulo: enquanto a
         * praça não tiver as três réguas declaradas, apurar recusa a competência
         * inteira e nada é gravado. É a mesma razão de a régua da cobrança ser o
         * lugar por onde a cobrança automática de uma base é acesa pela primeira
         * vez.
         */
        label: 'Configuração',
        items: [
          {
            label: 'Régua de comissão',
            to: '/sales/tiers',
            resource: 'sales.tiers',
            action: 'ver',
          },
          {
            label: 'Consultores e times',
            to: '/sales/consultants',
            resource: 'sales.teams',
            action: 'ver',
          },
        ],
      },
    ],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    icon: 'dinheiro',
    /*
     * Volta ao rail em 05/10/2026, com as duas telas de quem confere e paga: o
     * pagamento das premiações de recorrência e a despesa de venda. O lado do
     * consultor — "Meus pagamentos" e "Minhas despesas" — fica no Comercial,
     * que declara os dois `/mine` como prefixos mais longos que os daqui.
     *
     * As rotas continuam sob `/sales`, e os recursos continuam os mesmos:
     * mudar de menu não muda quem pode, nem quebra link de aviso gravado.
     *
     * Sem chave no módulo, pelo motivo do Comercial: são dois recursos, e a
     * chave de um apagaria o rail para quem só tem o outro.
     */
    prefixes: [
      '/sales/recurrence-payments',
      '/sales/expenses',
      '/finance/revenues',
      '/finance/payment-accounts',
      '/finance/expenses',
      '/finance/dashboard',
      '/finance/team-map',
    ],
    start: '/sales/recurrence-payments',
    sections: [
      {
        items: [
          /*
           * O painel (29/10/2026): a receita e a despesa lançadas, por cidade,
           * consultor e veículo. Primeiro do módulo: é a visão de cima.
           */
          {
            label: 'Painel',
            to: '/finance/dashboard',
            resource: 'finance.dashboard',
            action: 'ver',
          },
          /*
           * O mapa do time (29/10/2026): a receita e a despesa pela montagem
           * de hoje do comercial, em organograma ou em tabela. Logo abaixo do
           * Painel: é a mesma visão de cima, cortada pela hierarquia.
           */
          {
            label: 'Mapa do time',
            to: '/finance/team-map',
            resource: 'finance.team_map',
            action: 'ver',
          },
          /*
           * O quadro de despesas (`financeiro-03`, 26/10/2026): o sinistro e a
           * assistência que o supervisor do departamento aprovou. Antes de
           * Pagamentos Recorrência, como o roteiro pede.
           */
          {
            label: 'Despesas',
            to: '/finance/expenses',
            resource: 'finance.expenses',
            action: 'ver',
          },
          /*
           * O que a casa RECEBE (29/10/2026): a receita, que tomou o lugar das
           * Cobranças — a cobrança no Asaas é um ato sobre ela. Logo abaixo de
           * Despesas, a pedido do dono: as duas direções do dinheiro lado a lado.
           */
          {
            label: 'Receitas',
            to: '/finance/revenues',
            resource: 'finance.revenues',
            action: 'ver',
          },
          {
            label: 'Pagamentos Recorrência',
            to: '/sales/recurrence-payments',
            resource: 'management.payments',
            action: 'ver',
          },
          /* Fora do menu enquanto desativada — ver "Minhas despesas". */
          ...(SALE_EXPENSES_DISABLED_REASON === null
            ? [
                {
                  label: 'Despesas de Venda',
                  to: '/sales/expenses',
                  resource: 'sales.expenses',
                  action: 'ver',
                },
              ]
            : []),
          {
            label: 'Contas de recebimento',
            to: '/finance/payment-accounts',
            resource: 'finance.revenues',
            action: 'ver',
          },
        ],
      },
    ],
  },
  {
    id: 'sinistros',
    label: 'Sinistros',
    icon: 'escudo',
    prefixes: ['/claims'],
    start: '/claims',
    /*
     * "Aviso" é a lista de verdade, sobre a API, e cobra o recurso do ATO
     * (`claims.notices:ver`). Ela substituiu o quadro do Aviso em 24/09: as 12
     * fases do aviso eram roteamento entre pipes, e o sinistro nasce direto na
     * Análise.
     *
     * Os três quadros abaixo dela — Análise, Reparo e Indenização — rodam
     * sobre a API e exigem só `claims.module:ver`, a permissão de entrar no
     * módulo: a lista cobra `claims.notices:ver`, e cada ato da mesa cobra o
     * recurso do departamento na rota da API. Ver
     * `apps/api/src/modules/claims/permissions.catalog.ts`.
     *
     * As seções (14/10/2026, pedido do dono): a esteira — o aviso e os três
     * departamentos por onde ele corre —, os serviços avulsos, o desfecho, os
     * cadastros e a configuração. Benefícios entra quando houver tela. Acima
     * da esteira, a Gestão (23/10) e a Supervisão (28/10): as visões de quem
     * acompanha e aprova, e não de quem opera uma mesa.
     */
    sections: [
      /*
       * A Gestão (23/10/2026): o mapa de todos os departamentos, em cima de
       * tudo — é a visão de quem acompanha, e não a de quem opera uma mesa.
       * Periféricos ganhou a sua em 28/10/2026 (pedido do dono), sob o mesmo
       * recurso.
       */
      {
        label: 'Gestão',
        items: [
          {
            label: 'Gestão do sinistro',
            to: '/claims/management',
            resource: 'claims.management',
            action: 'ver',
          },
          {
            label: 'Gestão dos periféricos',
            to: '/claims/management/peripherals',
            resource: 'claims.management',
            action: 'ver',
          },
          /*
           * O mapa do time (29/10/2026): as ocorrências e a sinistralidade
           * pela montagem de hoje do comercial. Na Gestão, com recurso próprio
           * — mostra o número de cada pessoa do comercial.
           */
          {
            label: 'Mapa do time',
            to: '/claims/management/team-map',
            resource: 'claims.team_map',
            action: 'ver',
          },
        ],
      },
      /*
       * A Supervisão (28/10/2026, pedido do dono): o quadro do supervisor da
       * despesa saiu da esteira, porque não é um departamento por onde o
       * sinistro corre — é onde o supervisor aprova, para o Financeiro, a
       * despesa lançada nos cards do sinistro e de Periféricos. Fica em
       * Sinistros, e não no Financeiro, porque quem decide aqui é o supervisor
       * do departamento, antes de a despesa chegar lá; o Financeiro já tem a
       * sua "Despesas" (`/finance/expenses`), que é o passo seguinte.
       *
       * Um item só para os cinco departamentos (26/10/2026): quem supervisiona
       * qualquer um deles vê o item, e a tela mostra só os departamentos dele.
       *
       * A Supervisão do Reparo (29/10/2026, decisão do dono): a Supervisão e a
       * Aprovação Supervisor saíram do quadro do Reparo e moram aqui, só para
       * quem supervisiona o reparo.
       */
      {
        label: 'Supervisão',
        items: [
          {
            label: 'Supervisão do Reparo',
            to: '/claims/repair/supervision',
            resource: 'claims.repairs',
            action: 'supervisionar',
          },
          {
            label: 'Despesas',
            to: '/claims/expenses',
            resource: 'claims.notices',
            action: 'supervisionar_despesa',
            alternative: [
              { resource: 'claims.analyses', action: 'supervisionar_despesa' },
              { resource: 'claims.repairs', action: 'supervisionar_despesa' },
              { resource: 'claims.compensations', action: 'supervisionar_despesa' },
              { resource: 'claims.peripherals', action: 'supervisionar_despesa' },
            ],
          },
        ],
      },
      {
        label: 'Esteira de sinistros',
        items: [
          {
            label: 'Aviso',
            to: '/claims',
            resource: 'claims.notices',
            action: 'ver',
          },
          {
            label: 'Análise',
            to: '/claims/analysis/board',
            resource: 'claims.module',
            action: 'ver',
          },
          {
            label: 'Reparo',
            to: '/claims/repair/board',
            resource: 'claims.module',
            action: 'ver',
          },
          {
            label: 'Indenização',
            to: '/claims/compensation/board',
            resource: 'claims.module',
            action: 'ver',
          },
        ],
      },
      {
        label: 'Serviços & benefícios',
        items: [
          /* Sobre a API desde 08/10/2026, com o recurso do ATO. */
          {
            label: 'Periféricos',
            to: '/claims/peripherals',
            resource: 'claims.peripherals',
            action: 'ver',
          },
        ],
      },
      /*
       * Encerrados (13/10/2026): seção própria, e não um departamento — as
       * fases de desfecho de todos ficam aqui, e só aqui.
       */
      {
        label: 'Encerrados',
        items: [
          {
            label: 'Encerrados',
            to: '/claims/closed',
            resource: 'claims.closures',
            action: 'ver',
          },
        ],
      },
      /*
       * Os cadastros de apoio, com a permissão do ATO e não a do módulo: é a
       * primeira tela de Sinistros com backend de verdade, e quem não tem
       * `claims.third_parties:ver` receberia 403 na lista. Prestadores e
       * Instituições saíram daqui em 28/10/2026 — são da casa, e moram em
       * Plataforma → Cadastros; o terceiro é do sinistro, e fica.
       */
      {
        label: 'Cadastros',
        items: [
          {
            label: 'Terceiros',
            to: '/claims/third-parties',
            resource: 'claims.third_parties',
            action: 'ver',
          },
        ],
      },
      /* O SLA de cada fase (07/10/2026) — o vencimento é do card, na mesa. */
      {
        label: 'Configuração',
        items: [
          {
            label: 'SLA das fases',
            to: '/claims/phase-deadlines',
            resource: 'claims.deadlines',
            action: 'configurar',
          },
          /* Quem recebe o card ao entrar numa etapa (20/10/2026). */
          {
            label: 'Rodízio de responsáveis',
            to: '/claims/rotations',
            resource: 'claims.rotations',
            action: 'configurar',
          },
        ],
      },
    ],
  },
  {
    /*
     * A Assistência 24h (21/10/2026), que sai do Safegard: acionar, agendar,
     * os rascunhos da equipe, o pagamento aos prestadores e o cadastro deles.
     * Plano em `docs/modulos/assistencia/`. Desde 29/10/2026 Acionamentos é
     * uma tabela só — todos os status, com o status como filtro, e o novo num
     * modal —; a Agenda fica como a vista de calendário dos agendados.
     */
    id: 'assistencia',
    label: 'Assistência 24h',
    icon: 'assistencia',
    prefixes: ['/assistance'],
    start: '/assistance/dispatches',
    sections: [
      {
        items: [
          /* A Gestão da assistência (`assistencia-35`, 30/10/2026): o que a liderança lê. */
          {
            label: 'Gestão',
            to: '/assistance/management',
            resource: 'assistance.management',
            action: 'ver',
          },
          {
            label: 'Acionamentos',
            to: '/assistance/dispatches',
            resource: 'assistance.dispatches',
            action: 'ver',
          },
          {
            label: 'Agenda',
            to: '/assistance/agenda',
            resource: 'assistance.dispatches',
            action: 'ver',
          },
          {
            label: 'Pagamentos',
            to: '/assistance/settlements',
            resource: 'assistance.settlements',
            action: 'ver',
          },
        ],
      },
    ],
  },
  {
    /*
     * O intervalo diário de 15 minutos (09/10/2026). "Intervalos" não tem
     * `recurso`: a relação é de todo mundo logado — quem não acompanha a base
     * vê os próprios. Estouros e configuração somem em cascata.
     *
     * O consultor não tira intervalo (28/10/2026, pedido do dono): a relação
     * some para ele, e o módulo some junto se ele não acompanhar a base.
     */
    id: 'intervalo',
    label: 'Intervalos',
    icon: 'relogio',
    prefixes: ['/breaks'],
    start: '/breaks/records',
    sections: [
      {
        items: [
          { label: 'Intervalos', to: '/breaks/records', hiddenForConsultant: true },
          {
            label: 'Estourados',
            to: '/breaks/overruns',
            resource: 'breaks.overruns',
            action: 'ver',
            alternative: { resource: 'breaks.records', action: 'liderar' },
          },
          {
            label: 'Configuração',
            to: '/breaks/settings',
            resource: 'breaks.settings',
            action: 'editar',
            alternative: { resource: 'breaks.records', action: 'liderar' },
          },
        ],
      },
    ],
  },
  {
    id: 'plataforma',
    label: 'Plataforma',
    icon: 'plataforma',
    position: 'rodape',
    /*
     * `/portal/members` entra aqui, e não num módulo "Portal": ele é uma
     * tela só, e a regra 2 diz que módulo não existe para servir um item. Se o
     * portal ganhar as próprias telas de operação, ele sai daqui — e a linha a
     * mudar é o prefixo, não a estrutura.
     */
    prefixes: [
      '/users',
      '/groups',
      '/departments',
      '/sessions',
      '/preferences',
      '/audit',
      '/tenant-settings',
      '/integrations',
      '/contact-blocks',
      '/assistance-settings',
      '/portal',
      '/beneficiaries',
      '/companies',
      '/institutions',
      '/useful-links',
    ],
    start: '/users',
    sections: [
      {
        /*
         * Sem rótulo e sem `recurso`: os links úteis são de todo mundo da base
         * (07/10/2026), e ficam soltos no topo da barra — é o caso da primeira
         * seção, no docblock de `MenuSection`. Quem não administra nada
         * continua vendo-os, como vê "Minha conta".
         */
        items: [{ label: 'Links úteis', to: '/useful-links' }],
      },
      {
        label: 'Acesso',
        items: [
          {
            label: 'Usuários',
            to: '/users',
            resource: 'platform.users',
            action: 'ver',
          },
          {
            label: 'Grupos',
            to: '/groups',
            resource: 'platform.groups',
            action: 'ver',
          },
          {
            /*
             * Fica em "Acesso" e não numa seção própria: o departamento é a
             * fila de quem atende, e quem a administra administra pessoa e
             * grupo na mesma sessão de trabalho. Seção nova para um item só é
             * exatamente o que a regra 2 do cabeçalho recusa.
             */
            label: 'Departamentos',
            to: '/departments',
            resource: 'platform.departments',
            action: 'ver',
          },
          {
            label: 'Associados do portal',
            to: '/portal/members',
            resource: 'portal.members',
            action: 'ver',
          },
          {
            label: 'Trilha de auditoria',
            to: '/audit',
            resource: 'platform.events',
            action: 'ver',
          },
          {
            label: 'Configuração da base',
            to: '/tenant-settings',
            resource: 'platform.tenants',
            action: 'ver',
          },
          {
            label: 'Integrações',
            to: '/integrations',
            resource: 'platform.integrations',
            action: 'ver',
          },
          {
            label: 'Bloqueio de contato',
            to: '/contact-blocks',
            resource: 'platform.contact_blocks',
            action: 'ver',
          },
        ],
      },
      /*
       * A configuração da Assistência 24h (01/11/2026, pedido do dono): quem
       * aciona não precisa ver isto no meio da fila. A operação fica em
       * Assistência 24h, e o que muda a regra dela fica aqui.
       */
      {
        label: 'Assistência 24h',
        resource: 'assistance.dispatches',
        action: 'configurar',
        items: [
          {
            label: 'Cotas do regulamento',
            to: '/assistance-settings/quotas',
            resource: 'assistance.dispatches',
            action: 'configurar',
          },
          {
            label: 'Prazos do acompanhamento',
            to: '/assistance-settings/deadlines',
            resource: 'assistance.dispatches',
            action: 'configurar',
          },
          /* Os serviços de cada tipo, que o prestador marca como atendidos (02/11/2026). */
          {
            label: 'Serviços',
            to: '/assistance-settings/services',
            resource: 'assistance.dispatches',
            action: 'configurar',
          },
        ],
      },
      /*
       * Os cadastros da casa (28/10/2026, pedido do dono): quem presta serviço,
       * quem recebe pagamento da Indenização e a conta de pagamento de cada um.
       * Todo departamento usa, e nenhum é dono — Prestadores (até 02/11/2026,
       * "Empresas") e Instituições saíram de Sinistros, e Contas de pagamento
       * (até 02/11/2026, "Beneficiários") saiu de "Acesso", onde só estava para
       * não ter seção de um item. O que só a Assistência 24h sabe do prestador
       * (como paga, o raio, o preço) é a ação "Assistência 24h" da lista.
       */
      {
        label: 'Cadastros',
        items: [
          {
            label: 'Prestadores',
            to: '/companies',
            resource: 'claims.companies',
            action: 'ver',
          },
          {
            label: 'Instituições',
            to: '/institutions',
            resource: 'claims.companies',
            action: 'ver',
          },
          {
            label: 'Contas de pagamento',
            to: '/beneficiaries',
            resource: 'platform.beneficiaries',
            action: 'ver',
          },
        ],
      },
      {
        label: 'Minha conta',
        items: [
          { label: 'Minhas sessões', to: '/sessions' },
          { label: 'Trocar a senha', to: '/password' },
          // sem `recurso`: preferência é da própria pessoa, e não há a quem
          // conceder o poder de mexer na dos outros
          { label: 'Preferências', to: '/preferences' },
        ],
      },
    ],
  },
];

/**
 * As rotas que existem de propósito sem item de menu, e por quê.
 *
 * Uma tela sem caminho até ela é uma tela que ninguém abre — e o defeito não dá
 * erro nenhum: a rota responde, o componente renderiza, e a funcionalidade
 * simplesmente não existe para quem usa. `registry.test.ts` cobra rota
 * autenticada × item de menu, e é aqui que a exceção fica escrita.
 *
 * Detalhe de lista não é exceção preguiçosa: chega-se a ele pela lista, e um
 * item de menu para `/tickets/$id` não teria id nenhum para apontar.
 */
export const NO_MENU_ITEM: Record<string, string> = {
  // com aspas: `registry.test.ts` só lê chave entre aspas, e o Prettier as tiraria de `$`
  // prettier-ignore
  '$': 'curinga: o "não encontrado" de qualquer endereço que não existe, não uma tela',
  '/tickets/$id': 'detalhe: chega-se a ele pela lista, não pelo menu',
  '/assistance/new':
    'endereço antigo do novo acionamento (até 29/10/2026): só redireciona para o modal em /assistance/dispatches, por link salvo',
  '/assistance/drafts':
    'endereço antigo dos rascunhos (até 29/10/2026): só redireciona para /assistance/dispatches filtrado em rascunho, por link salvo',
  '/tickets/board':
    'modo da lista: chega-se a ele pelo botão "Ver quadro" de Tickets, e volta por "Ver lista" (pedido do dono, 29/10/2026)',
  '/portal/members/$id': 'detalhe: chega-se a ele pela lista, não pelo menu',
  '/retention/requests/$id': 'detalhe: chega-se a ele pelo quadro, não pelo menu',
  '/erp/veiculos/$code': 'detalhe: chega-se a ele pela lista de veículos, não pelo menu',
  '/erp/produtos/$code': 'detalhe: chega-se a ele pela lista de produtos, não pelo menu',
  '/erp/boletos/$code': 'detalhe: chega-se a ele pela lista de boletos, não pelo menu',
  '/erp/alteracoes/$subject/$code':
    'detalhe: chega-se a ele pelo botão "Ver" da lista de alterações, não pelo menu',
  '/erp/atendimentos/$code':
    'detalhe: chega-se a ele pelo botão "Ver" da lista de atendimentos, não pelo menu',
  '/erp/associados/$code':
    'detalhe: chega-se a ele pela lista de associados e pelas fichas de veículo e boleto, não pelo menu',
  '/claims/third-parties/$id':
    'detalhe: chega-se a ele pela lista de terceiros, não pelo menu',
  '/claims/$id': 'detalhe: chega-se a ele pela lista de sinistros, não pelo menu',
  '/claims/providers':
    'endereço antigo de Empresas (até 28/10/2026): só redireciona para /companies, por link salvo e aviso gravado',
  '/assistance/expenses':
    'endereço antigo do quadro do supervisor da despesa (até 02/11/2026): virou a aba "Aprovação do supervisor" de /assistance/settlements, e só redireciona',
  '/assistance/providers':
    'endereço antigo dos prestadores da Assistência 24h (até 30/10/2026): o prestador é a empresa, e a seção mora na ação "Assistência 24h" de /companies — só redireciona',
  '/finance/charges':
    'endereço antigo de Cobranças (até 29/10/2026): só redireciona para /finance/revenues, por link salvo e aviso gravado',
  '/claims/institutions':
    'endereço antigo de Instituições (até 28/10/2026): só redireciona para /institutions, por link salvo e aviso gravado',
  '/sales/portfolio/members/$code':
    'detalhe: chega-se a ele pela agenda de clientes (/sales/portfolio), não pelo menu',
  '/billing/awards/operator/$operatorId':
    'detalhe: chega-se a ele pelo nome do operador no painel da premiação, não pelo menu',
  '/sales/management':
    'o Painel de Gestão saiu em 28/10/2026 — o endereço só redireciona para o ' +
    'Início do Comercial, que assumiu o papel dele.',
  '/sales/management/me':
    'o painel do próprio consultor saiu em 28/10/2026 — redireciona para o Início.',
  '/sales/management/consultant':
    'o painel de outro consultor saiu em 28/10/2026 — redireciona para o Início ' +
    'daquela pessoa (/sales/home?consultant=).',
  '/sales/management/portfolio':
    'detalhe de um número: chega-se a ela clicando na premiação apurada do painel. ' +
    'Sem premiação e sem competência na busca ela não tem o que listar, e um item ' +
    'de menu levaria a uma tela que só sabe dizer que falta parâmetro.',
};

/* ------------------------------------------------------------------------- *
 * O que a tela pergunta a este arquivo
 * ------------------------------------------------------------------------- */

/** Quem decide se a pessoa vê. Assinatura mínima, para o filtro ser testável. */
export interface Asker {
  permissions: string[];
  /** Ver `MenuItem.consultantOnly`. Ausente vale como "não é consultor". */
  consultant?: boolean;
}

const granted = (actor: Asker, resource?: string, action?: string): boolean =>
  !resource || canAccess(actor.permissions, resource, action ?? 'ver');

/**
 * Os módulos que esta pessoa enxerga, já sem o que ela não pode.
 *
 * A cascata é o ponto: seção que perdeu todos os itens não desenha rótulo
 * órfão, e módulo que perdeu todas as seções **não aparece no rail**. Sem ela,
 * quem só atende cobrança veria um ícone de Comercial que abre uma barra vazia
 * — e "vazio" e "não tenho permissão" são coisas diferentes que a tela estaria
 * dizendo com o mesmo desenho.
 *
 * Nada aqui protege: quem protege é o `PermissionGuard`. Esconder é cortesia.
 */
export function visibleModules(
  actor: Asker,
  modules: MenuModule[] = MODULES,
): MenuModule[] {
  return (
    modules
      .filter((m) => granted(actor, m.resource, m.action))
      .map((m) => ({
        ...m,
        sections: m.sections
          .filter((s) => granted(actor, s.resource, s.action))
          .map((s) => ({
            ...s,
            items: s.items.filter(
              (i) =>
                (granted(actor, i.resource, i.action) ||
                  ([] as readonly MenuGrant[])
                    .concat(i.alternative ?? [])
                    .some((g) => granted(actor, g.resource, g.action))) &&
                (!i.consultantOnly || actor.consultant === true) &&
                (!i.hiddenForConsultant || actor.consultant !== true),
            ),
          }))
          .filter((s) => s.items.length > 0),
      }))
      .filter((m) => m.sections.length > 0)
      /*
       * O `inicio` também entra na cascata — era o buraco dela.
       *
       * A poda acima tirava seções e itens e deixava `inicio` como estava. Em
       * `plataforma`, que não tem `recurso` e sobrevive pela seção "Minha conta"
       * (sem `recurso` nenhum), o ícone do rail continuava apontando para
       * `/usuarios`: quem não tem `platform.users:ver` clicava e caía num
       * 403 — a interface oferecendo uma porta que o guard fecha, com a tela
       * dizendo "tente de novo em instantes" para um problema que tentar não
       * resolve. Esconder é cortesia; oferecer o que nega é o contrário dela.
       */
      .map((m) => {
        const reachable = m.sections.flatMap((s) => s.items.map((i) => i.to));
        return reachable.includes(m.start) ? m : { ...m, start: reachable[0]! };
      })
      /*
       * Para o consultor, Comercial vem primeiro no rail (28/10/2026, pedido
       * do dono) — é onde fica o Início dele. `sort` é estável: o resto
       * mantém a ordem do array.
       */
      .sort((a, b) =>
        actor.consultant === true
          ? Number(b.id === 'comercial') - Number(a.id === 'comercial')
          : 0,
      )
  );
}

/**
 * Qual módulo está aberto.
 *
 * Pelo prefixo mais específico, e não pelo primeiro que casar: `/portal` e `/`
 * casariam os dois com a raiz se a comparação fosse ingênua, e a ordem do
 * array passaria a decidir o que a pessoa vê.
 */
export function activeModule(path: string, modules: MenuModule[]): MenuModule | null {
  let best: { module: MenuModule; size: number } | null = null;
  for (const module of modules) {
    for (const prefix of module.prefixes) {
      if (!within(path, prefix)) continue;
      if (best === null || prefix.length > best.size) {
        best = { module, size: prefix.length };
      }
    }
  }
  return best?.module ?? null;
}

/**
 * Qual item da barra está aceso — um, ou nenhum.
 *
 * `activeRoute` vem de `@evogard/domain` e vale para as duas SPAs. Em
 * `/tickets/categories` ela responde "Categorias"; numa tela de detalhe sem
 * item próprio, responde o item que a contém.
 */
export function activeItem(path: string, module: MenuModule | null): string | null {
  if (!module) return null;
  return activeRoute(
    path,
    module.sections.flatMap((s) => s.items.map((i) => i.to)),
  );
}
